import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TooltipItem, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { trackEvent } from 'utils/analytics';
import { RevenueRentalType } from 'types/TransactionReport';
import { SpotInterface } from 'types';
import { useRevenueContext } from 'contexts/RevenueContext';
import { HTTPError } from 'clients/HTTPClient';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import { compactCurrency } from 'utils/currency';
import LinesIcon from 'assets/icons/lines.svg?react';
import Loader from 'components/Loader/Loader';
import WidgetBox from 'components/widgets/WidgetBox';
import AGMessage from 'components/AGMessage/AGMessage';
import AGMultipleSelect from 'components/AGSelect/AGMultipleSelect/AGMultipleSelect';
import Label from 'components/AGForm/Inputs/Label/Label';
import {
    ChartId,
    calculateRevenuePercentage,
    filteredLegendOptions,
    getDataForByRentalTypeGraph,
    getRentalTypeOptions,
    getSelectableRentalTypes,
    reportHasTicketRevenue,
    resolveSelectedRentalTypes,
} from './helpers';

const options: ChartOptions<'bar'> = {
    scales: {
        x: { stacked: true },
        y: {
            stacked: true,
            grid: { display: true, borderDash: [2] },
            ticks: {
                callback(tickValue) {
                    return typeof tickValue === 'number' ? compactCurrency.format(tickValue) : '';
                },
                maxTicksLimit: 4,
            },
        },
    },
    plugins: {
        tooltip: {
            mode: 'x',
            filter(tooltipItem: TooltipItem<'bar'>): boolean {
                return Boolean(tooltipItem.raw);
            },
            callbacks: {
                label({ chart, raw, dataset }: TooltipItem<'bar'>): string | string[] {
                    const percentage = chart.tooltip
                        ? calculateRevenuePercentage(
                              chart.tooltip.dataPoints.map((dp) => dp.raw as number),
                              raw as number
                          )
                        : 0;
                    return `${dataset.label} - ${compactCurrency.format(raw as number)} (${percentage}%)`;
                },
            },
            borderColor: 'transparent',
        },
        legend: filteredLegendOptions,
    },
};

type RevenueByRentalTypeProps = {
    spot: SpotInterface;
    filters: { rentalType: RevenueRentalType };
    urlUpdater: UrlUpdaterFunctions<{
        rentalType: RevenueRentalType;
    }>;
};

function RevenueByRentalType({ spot, filters, urlUpdater }: RevenueByRentalTypeProps): JSX.Element {
    const { getRevenueByRentalTypeReport, revenueByRentalTypeReport: report } = useRevenueContext();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [graphData, setGraphData] = useState<
        | ({
              labels: string[];
          } & { [key in RevenueRentalType]: { data: number[]; backgroundColor: string; label: string } })
        | null
    >(null);

    const getRevenueData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getRevenueByRentalTypeReport(spot.uuid);
            setGraphData(getDataForByRentalTypeGraph(response));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid]);

    useEffect(() => {
        getRevenueData();
    }, [spot.uuid]);

    const hasTicketRevenue = reportHasTicketRevenue(report);
    const selectableRentalTypes = useMemo(() => getSelectableRentalTypes(hasTicketRevenue), [hasTicketRevenue]);
    const rentalTypeOptions = useMemo(() => getRentalTypeOptions(hasTicketRevenue), [hasTicketRevenue]);
    const selectedRentalTypes = useMemo(
        () => resolveSelectedRentalTypes(filters.rentalType, hasTicketRevenue),
        [filters.rentalType, hasTicketRevenue]
    );

    const datasets = useMemo(() => {
        if (!graphData) return [];
        // Iterate the selectable types, not the selection, so stacking order stays canonical
        // regardless of the order the filter happens to list them in.
        return selectableRentalTypes
            .filter((rentalType) => selectedRentalTypes.includes(rentalType))
            .map((rentalType) => graphData[rentalType]);
    }, [graphData, selectableRentalTypes, selectedRentalTypes]);

    function handleRentalTypeSelection(selection: string[]) {
        let newSelection = selection;
        // Is user last selected "All" we should unselect the rest of the options:
        if (selection[selection.length - 1] === '') {
            urlUpdater.rentalType('');
            newSelection = ['All'];
        } else urlUpdater.rentalType(selection.join(','));

        trackEvent('Changed Rental Type', { spot: spot.pk, spotName: spot.name, rentalType: newSelection });
    }

    // When everything selectable is selected, also tick the "All" option ('').
    const selectedRentalType = useMemo(
        () =>
            selectedRentalTypes.length === selectableRentalTypes.length
                ? ['', ...selectedRentalTypes]
                : selectedRentalTypes,
        [selectedRentalTypes, selectableRentalTypes]
    );

    return (
        <WidgetBox title="Gross Revenue by Type">
            <>
                <div>
                    <Label label="Rental Type" />
                    <AGMultipleSelect
                        selectedValues={selectedRentalType}
                        onSelect={handleRentalTypeSelection}
                        options={rentalTypeOptions}
                        icon={<LinesIcon width={32} />}
                        maxHeight="300px"
                    />
                </div>
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                {loading || !report || !graphData ? (
                    <Loader centered style={{ marginTop: '20px' }} />
                ) : (
                    <div id={ChartId.RENTAL_TYPE_CHART} style={{ paddingBlock: '32px' }}>
                        <Bar
                            data={{
                                labels: graphData.labels,
                                datasets,
                            }}
                            options={options}
                            style={{ maxHeight: '260px' }}
                        />
                    </div>
                )}
            </>
        </WidgetBox>
    );
}

export default RevenueByRentalType;
